
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { getWorkDetail } from '../api';
import HomeFooter from '../components-pc/HomeFooter.vue';
import PcPageLoading from '../components-pc/PcPageLoading.vue';

interface DetailMetaItem {
  label: string;
  value: string;
}

interface ReportRow {
  imageUrl: string;
  description: string;
}

interface DetailAttachment {
  label: string;
  name: string;
  url: string;
}

interface DetailWork {
  id: string;
  title: string;
  date: string;
  time: string;
  status: string;
  statusClass: string;
  workType: number;
  workTypeName: string;
  audioType: number;
  isVideo: boolean;
  coverUrl: string;
  videoUrl: string;
  imageUrls: string[];
  reportRows: ReportRow[];
  attachments: DetailAttachment[];
  desc: string;
  expUrl: string;
  author: string;
  school: string;
  teacher: string;
  className: string;
  education: string;
  contact: string;
}

const baseUrl = 'cqcc';
const emptyWork: DetailWork = {
  id: '',
  title: '',
  date: '',
  time: '',
  status: '',
  statusClass: 'status-pending',
  workType: 0,
  workTypeName: '',
  audioType: 0,
  isVideo: false,
  coverUrl: '',
  videoUrl: '',
  imageUrls: [],
  reportRows: [],
  attachments: [],
  desc: '',
  expUrl: '',
  author: '',
  school: '',
  teacher: '',
  className: '',
  education: '',
  contact: '',
};

const workTypeNameMap: { [key: number]: string } = {
  1: '摄影',
  2: '视听',
  3: '短视频',
  4: '图文报道',
  5: '人工智能',
};

const audioTypeNameMap: { [key: number]: string } = {
  1: '长图作品',
  2: '海报作品',
  3: 'H5作品',
};

export default Vue.extend({
  name: 'DetailPc',
  components: {
    HomeFooter,
    PcPageLoading,
  },
  data() {
    return {
      loading: false,
      currentWorkData: null as DetailWork | null,
      activeImageIndex: 0,
      imagePreviewVisible: false,
      previewImageIndex: 0,
      imageSwiperOptions: {
        loop: false,
        speed: 420,
        slidesPerView: 1,
        resistanceRatio: 0,
        watchOverflow: true,
      },
    };
  },
  computed: {
    currentWork(): DetailWork {
      return this.currentWorkData || emptyWork;
    },
    imageSlides(): string[] {
      return this.currentWork.imageUrls;
    },
    previewImageUrl(): string {
      return this.imageSlides[this.previewImageIndex] || '';
    },
    detailMetaList(): DetailMetaItem[] {
      const work = this.currentWork;
      return [
        { label: '作品类型', value: this.getWorkTypeDisplayName(work) },
        { label: '作者', value: work.author },
        { label: '指导教师', value: work.teacher },
        { label: '所属院校', value: work.school },
        { label: '年级', value: work.className },
        { label: '学历', value: work.education },
        { label: '联系方式', value: work.contact },
      ].filter((item) => item.value);
    },
    showDescription(): boolean {
      return (
        this.currentWork.workType !== 2 ||
        (this.currentWork.workType === 2 &&
          [2, 3].includes(this.currentWork.audioType))
      );
    },
    descriptionTitle(): string {
      return this.currentWork.workType === 2 &&
        [2, 3].includes(this.currentWork.audioType)
        ? '作品说明'
        : '简介';
    },
  },
  watch: {
    '$route.query.id'() {
      this.fetchWorkDetail();
    },
  },
  mounted() {
    this.fetchWorkDetail();
  },
  beforeDestroy() {
    this.unlockPageScroll();
    window.removeEventListener('keydown', this.onPreviewKeydown);
  },
  methods: {
    getRouteId(): string {
      const id = this.$route.query.id;
      return Array.isArray(id) ? String(id[0] || '') : String(id || '');
    },
    getDetailPayload(res: any): any {
      return (
        (res && res.data && res.data.data) || (res && res.data) || res || {}
      );
    },
    async fetchWorkDetail() {
      const id = this.getRouteId();
      if (!id) {
        this.closeImagePreview();
        this.currentWorkData = null;
        return;
      }

      this.closeImagePreview();
      this.loading = true;
      try {
        const res: any = await getWorkDetail({ id });
        const detail = this.getDetailPayload(res);
        this.currentWorkData = this.normalizeWorkDetail(detail);
        this.resetImageCarousel();
      } catch (err) {
        console.error('作品详情获取失败：', err);
        this.currentWorkData = null;
        message.error('作品详情获取失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    normalizeWorkDetail(detail: any): DetailWork {
      const workType = Number(detail.work_type || detail.workType || 0);
      const audioType = Number(detail.audio_type || detail.audioType || 0);
      const isVideoWork =
        [3, 5].includes(workType) || this.$route.query.type === 'video';
      const fileList = this.parseFileList(detail.files);
      const reportRows =
        workType === 4 ? this.parseReportRows(detail.files) : [];
      const imageUrls =
        workType === 4
          ? reportRows.map((item: ReportRow) => item.imageUrl)
          : this.getImageUrls(workType, detail, fileList);
      const audioFileUrl = workType === 2 ? this.getFileUrl(fileList[0]) : '';
      const isAudioVideo =
        workType === 2 && audioType === 2 && this.isVideoUrl(audioFileUrl);
      const videoUrl =
        isVideoWork || isAudioVideo ? this.getFileUrl(fileList[0]) : '';
      const coverUrl = isVideoWork
        ? this.parseUrlList(detail.explanation)[0] ||
          String(detail.explanation || '')
        : isAudioVideo
        ? ''
        : imageUrls[0] || '';
      const dateTime = this.formatDateTime(
        detail.createtime || detail.createTime,
      );

      return {
        id: String(detail.id || this.getRouteId()),
        title: detail.title || '未命名作品',
        date: dateTime.date,
        time: dateTime.time,
        status: this.getStatusText(detail.enable),
        statusClass: this.getStatusClass(detail.enable),
        workType,
        workTypeName: workTypeNameMap[workType] || '',
        audioType,
        isVideo: isVideoWork || isAudioVideo,
        coverUrl,
        videoUrl,
        imageUrls,
        reportRows,
        attachments: this.getAttachments(workType, audioType, detail),
        desc: detail.introduction || '暂无简介',
        expUrl: detail.exp_url || detail.expUrl || '',
        author: detail.name || '',
        school: detail.school || '',
        teacher: detail.teacher || '',
        className: detail.class || '',
        education: detail.education || '',
        contact:
          detail.contact ||
          detail.contact_way ||
          detail.contactWay ||
          detail.phone ||
          detail.mobile ||
          '',
      };
    },
    getImageUrls(workType: number, detail: any, fileList: any[]): string[] {
      if ([3, 5].includes(workType)) {
        return [];
      }
      if (workType === 2) {
        return fileList
          .map((item: any) => this.getFileUrl(item))
          .filter((url: string) => url && !this.isVideoUrl(url));
      }

      const fileUrls = fileList
        .map((item: any) => this.getFileUrl(item))
        .filter(Boolean);
      if (fileUrls.length) {
        return fileUrls;
      }

      return this.parseUrlList(detail.explanation);
    },
    getAttachments(
      workType: number,
      audioType: number,
      detail: any,
    ): DetailAttachment[] {
      if (workType !== 2) {
        return [];
      }

      const label =
        audioType === 1
          ? '作品说明'
          : audioType === 2 || audioType === 3
          ? 'PSD/AI等源文件'
          : '相关文件';

      const attachments = this.parseUrlList(detail.explanation).map(
        (url: string) => ({
          label,
          name: this.getFileNameFromUrl(url) || label,
          url,
        }),
      );

      if (audioType === 3) {
        attachments.push(
          ...this.parseUrlList(detail.material).map((url: string) => ({
            label: '录屏素材',
            name: this.getFileNameFromUrl(url) || '录屏素材',
            url,
          })),
        );
      }

      return attachments;
    },
    getWorkTypeDisplayName(work: DetailWork): string {
      if (work.workType !== 2) {
        return work.workTypeName;
      }
      const audioTypeName = audioTypeNameMap[work.audioType];
      return audioTypeName
        ? `${work.workTypeName} / ${audioTypeName}`
        : work.workTypeName;
    },
    parseFileList(value: any): any[] {
      if (Array.isArray(value)) {
        return value;
      }
      if (!value) {
        return [];
      }
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (err) {
        return [value];
      }
    },
    parseUrlList(value: any): string[] {
      return this.parseFileList(value)
        .map((item: any) => this.getFileUrl(item))
        .filter(Boolean);
    },
    parseReportRows(value: any): ReportRow[] {
      return this.parseFileList(value)
        .map((item: any) => {
          const imageUrl = this.getFileUrl(item);
          if (!imageUrl) {
            return null;
          }

          return {
            imageUrl,
            description:
              typeof item === 'string'
                ? ''
                : String(item.description || item.explanation || ''),
          };
        })
        .filter(Boolean) as ReportRow[];
    },
    getFileUrl(item: any): string {
      if (!item) {
        return '';
      }
      if (typeof item === 'string' || typeof item === 'number') {
        return String(item);
      }
      if (typeof item === 'object') {
        return String(
          item.imageUrl ||
            item.image_url ||
            item.url ||
            item.fileUrl ||
            item.file_url ||
            item.path ||
            '',
        );
      }
      return '';
    },
    getFileNameFromUrl(url: string): string {
      const path = url.split('?')[0].split('#')[0];
      try {
        return decodeURIComponent(path.split('/').pop() || '');
      } catch (err) {
        return path.split('/').pop() || '';
      }
    },
    isVideoUrl(url: string): boolean {
      return /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(url);
    },
    formatDateTime(value: any): { date: string; time: string } {
      if (!value) {
        return { date: '', time: '' };
      }

      const normalized = String(value).replace(/\//g, '-');
      const [date = '', time = ''] = normalized.split(/\s+/);

      return {
        date: date.replace(/-/g, '.'),
        time: time.slice(0, 5),
      };
    },
    getStatusText(enable: string | number) {
      const status = Number(enable);

      if (status === 1) {
        return '审核已通过';
      }

      if (status === -1) {
        return '审核未通过';
      }

      return '待审核';
    },
    getStatusClass(enable: string | number) {
      const status = Number(enable);

      if (status === -1) {
        return 'status-failed';
      }

      if (status === 1) {
        return 'status-passed';
      }

      return 'status-pending';
    },
    getBackgroundStyle(url: string) {
      if (!url) {
        return undefined;
      }

      return {
        backgroundImage: `url(${url})`,
      };
    },
    openExternal(url: string) {
      if (!url) {
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    openImagePreview(index: number) {
      if (!this.imageSlides[index]) {
        return;
      }
      this.previewImageIndex = index;
      this.imagePreviewVisible = true;
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this.onPreviewKeydown);
    },
    closeImagePreview() {
      this.imagePreviewVisible = false;
      this.unlockPageScroll();
      window.removeEventListener('keydown', this.onPreviewKeydown);
    },
    unlockPageScroll() {
      document.body.style.overflow = '';
    },
    goPreviewImage(index: number) {
      const slideCount = this.imageSlides.length;
      if (!slideCount) {
        return;
      }
      this.previewImageIndex = (index + slideCount) % slideCount;
    },
    onPreviewKeydown(event: KeyboardEvent) {
      if (!this.imagePreviewVisible) {
        return;
      }
      if (event.key === 'Escape') {
        this.closeImagePreview();
      }
      if (event.key === 'ArrowLeft') {
        this.goPreviewImage(this.previewImageIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        this.goPreviewImage(this.previewImageIndex + 1);
      }
    },
    goImage(index: number) {
      const slideCount = this.imageSlides.length;
      if (!slideCount) {
        return;
      }
      const nextIndex = (index + slideCount) % slideCount;
      this.activeImageIndex = nextIndex;
      this.syncImageSwiper();
    },
    onImageSlideChange() {
      const swiper = this.getImageSwiper();
      if (swiper) {
        this.activeImageIndex = swiper.realIndex || swiper.activeIndex || 0;
      }
    },
    resetImageCarousel() {
      this.activeImageIndex = 0;
      this.syncImageSwiper();
    },
    syncImageSwiper() {
      this.$nextTick(() => {
        const swiper = this.getImageSwiper();
        if (swiper && swiper.activeIndex !== this.activeImageIndex) {
          swiper.slideTo(this.activeImageIndex);
        }
      });
    },
    getImageSwiper(): any {
      const imageSwiper = this.$refs.imageSwiper as Vue & {
        swiperInstance?: any;
      };
      if (imageSwiper && imageSwiper.swiperInstance) {
        return imageSwiper.swiperInstance;
      }
      return null;
    },
    goWorks() {
      this.$router.push(`/${baseUrl}/works`).catch((err: Error) => err);
    },
    goSignup() {
      this.$router.push(`/${baseUrl}/signup`).catch((err: Error) => err);
    },
    goEdit() {
      const editPathMap: { [key: number]: string } = {
        1: `/${baseUrl}/signup/photo`,
        2: `/${baseUrl}/signup/audio`,
        3: `/${baseUrl}/signup/video`,
        4: `/${baseUrl}/signup/report`,
        5: `/${baseUrl}/signup/ai`,
      };
      const path = editPathMap[this.currentWork.workType];
      if (!path) {
        message.error('暂不支持编辑该作品类型');
        return;
      }

      this.$router
        .push({
          path,
          query: {
            id: this.currentWork.id,
          },
        })
        .catch((err: Error) => err);
    },
    handleDelete() {
      message.info('删除功能暂未开放');
    },
  },
});
